import { CAUSE } from "./deviceCheckRules"
// `MISS` vit avec la règle qui le produit — ce qui invalide le souvenir se lit
// d'un seul fichier.
import { MISS } from "./verifiedMemory"

/**
 * Le texte affiché sous chaque ligne de verdict, regroupé ici pour que les
 * formulations vues par l'élève ne soient pas éparpillées dans le rendu.
 *
 * Écrit pour un élève de dix ans : une phrase courte, des mots qu'il connaît.
 * Une même cause ne se dit pas pareil selon la plateforme : sur web c'est le
 * navigateur qui bloque, sur mobile c'est un réglage du téléphone.
 */

const CAMERA = {
    [CAUSE.NOT_ASKED]: () => "Il faut ton accord",
    [CAUSE.GRANTED]: () => "On te voit",
    [CAUSE.DENIED]: (isWeb) =>
        isWeb ? "Ton navigateur bloque la caméra" : "La caméra est éteinte dans les réglages",
    [CAUSE.BLOCKED_BY_SYSTEM]: () => "Ton ordinateur bloque la caméra",
    [CAUSE.NO_DEVICE]: () => "Je ne trouve pas de caméra",
    [CAUSE.IN_USE]: () => "Une autre appli l'utilise",
    [CAUSE.DARK_IMAGE]: () => "L'image est toute noire",
    [CAUSE.UNKNOWN]: () => "Je n'arrive pas à l'allumer",
}

const MICROPHONE = {
    [CAUSE.NOT_ASKED]: () => "Il faut ton accord",
    [CAUSE.GRANTED]: (isWeb, hasSignal) => (hasSignal ? "On t'entend" : "Je n'entends rien"),
    [CAUSE.DENIED]: (isWeb) =>
        isWeb ? "Ton navigateur bloque le micro" : "Le micro est éteint dans les réglages",
    [CAUSE.BLOCKED_BY_SYSTEM]: () => "Ton ordinateur bloque le micro",
    [CAUSE.NO_DEVICE]: () => "Je ne trouve pas de micro",
    [CAUSE.IN_USE]: () => "Une autre appli l'utilise",
    [CAUSE.UNKNOWN]: () => "Je n'arrive pas à l'allumer",
}

export function cameraDetail(cause, isWeb) {
    return (CAMERA[cause] ?? CAMERA[CAUSE.UNKNOWN])(isWeb)
}

export function microphoneDetail(cause, isWeb, hasSignal) {
    return (MICROPHONE[cause] ?? MICROPHONE[CAUSE.UNKNOWN])(isWeb, hasSignal)
}

/**
 * Pourquoi on refait le test à qui l'a déjà fait.
 *
 * Sans cette phrase, l'élève — ou le tuteur — qui a passé le parcours la veille
 * et le retrouve conclut que l'écran est cassé. La raison tient en une ligne et
 * suffit : le souvenir ne vaut que pour une configuration donnée, et ce qui l'a
 * fait tomber est nommé.
 *
 * Rien à dire à qui n'avait pas de souvenir — c'est le cas de tout premier
 * passage. Rien non plus quand la configuration ne se lit pas : l'accueil
 * annonce déjà que le navigateur va redemander l'accès, et c'est le geste qui
 * compte.
 */
const MEMORY_MISS = {
    [MISS.CHANGED]: "Ton micro, ta caméra ou ton navigateur ont changé depuis ton dernier test : on revérifie.",
    [MISS.EXPIRED]: "Ton dernier test date de plus d'un mois : on revérifie.",
}

export function memoryMissDetail(reason) {
    return MEMORY_MISS[reason] ?? ""
}

/**
 * Le nom du navigateur tel que l'élève le connaît, pour lui parler de « Chrome »
 * plutôt que de « ton navigateur » quand on le sait.
 */
const BROWSER_LABEL = {
    chrome: "Chrome",
    edge: "Edge",
    firefox: "Firefox",
    safari: "Safari",
}

export function browserLabel(family) {
    return BROWSER_LABEL[family] ?? "Ton navigateur"
}

/** Le même, en milieu de phrase : « Chrome », ou « ton navigateur ». */
export function browserLabelInSentence(family) {
    return BROWSER_LABEL[family] ?? "ton navigateur"
}

/**
 * Qui est devant l'écran, et donc qui est « de l'autre côté ».
 *
 * L'écran parle à un élève : « ton tuteur te voit ». Le tuteur passe par le
 * même écran depuis sa carte de séance, et lisait qu'il allait être vu par son
 * tuteur. Seules les phrases qui nomment l'autre côté changent ; le tutoiement
 * reste, comme partout dans l'app.
 */
export const AUDIENCE = { STUDENT: "student", TUTOR: "tutor" }

const STUDENT_COPY = {
    // Où l'on est, en une ligne, et ce qu'on fait, en une autre. Retour de
    // l'équipe Ops au contact des familles (03/10) : l'écran se lisait comme
    // un questionnaire, et l'élève croyait être déjà dans son live. Le mot
    // est « live », celui de l'en-tête et de l'équipe.
    title: "Avant d'entrer dans ton live",
    lead: "On vérifie que tout marche. 30 secondes, rien à remplir.",
    checking: "Vérification avant ton live",
    promise: "Juste après, tu entres dans ton live : ton tuteur t'attend.",
    cta: "Vérifier mon matériel",
    remembered: "Caméra et micro déjà vérifiés",
    enterNow: "Entrer dans mon live",
    enterBefore: "Entrer sans vérifier",
    enterSkip: "Entrer sans finir",
    needConsent: "Pour que ton tuteur te voie et t'entende, il faut ton accord pour la caméra et le micro. Clique sur « Autoriser ».",
    cameraGiveUp: "Ça ne marche toujours pas ? Entre quand même : ton tuteur t'entendra, et tu pourras réessayer la caméra pendant le live.",
    videoOff: "Ta vidéo est coupée. Allume-la, et ton tuteur pourra te voir.",
    otherNetwork: "Demande à un adulte d'activer le partage de connexion d'un téléphone.",
}

const TUTOR_COPY = {
    title: "Avant d'entrer dans ton live",
    lead: "On vérifie que tout marche. 30 secondes, rien à remplir.",
    checking: "Vérification avant ton live",
    promise: "Juste après, tu entres dans ton live : tes élèves t'attendent.",
    cta: "Vérifier mon matériel",
    remembered: "Caméra et micro déjà vérifiés",
    enterNow: "Entrer dans mon live",
    enterBefore: "Entrer sans vérifier",
    enterSkip: "Entrer sans finir",
    needConsent: "Pour que tes élèves te voient et t'entendent, il faut ton accord pour la caméra et le micro. Clique sur « Autoriser ».",
    cameraGiveUp: "Ça ne marche toujours pas ? Entre quand même : tes élèves t'entendront, et tu pourras réessayer la caméra pendant le live.",
    videoOff: "Ta vidéo est coupée. Allume-la, et tes élèves pourront te voir.",
    otherNetwork: "Active le partage de connexion de ton téléphone.",
}

export const audienceCopy = (audience) => (audience === AUDIENCE.TUTOR ? TUTOR_COPY : STUDENT_COPY)
